import type { MediaAsset } from "../domain/types";
import type { MediaUploadAdapter } from "./uploadAdapter";
import { localIndexedDbAdapter } from "./localIndexedDbAdapter";
import {
  assertSameStorageKey,
  buildStorageKey,
  isHttpsUrl,
} from "./storageKey";
import { MediaUploadError } from "./signTypes";
import { takeHybridFallbackError } from "./hybridUploadAdapter";

let activeAdapter: MediaUploadAdapter = localIndexedDbAdapter;

export function setMediaUploadAdapter(adapter: MediaUploadAdapter): void {
  activeAdapter = adapter;
}

export function getMediaUploadAdapter(): MediaUploadAdapter {
  return activeAdapter;
}

export function getActiveMediaAdapterId(): string {
  return activeAdapter.id;
}

function formatUploadError(err: unknown): string {
  if (err instanceof MediaUploadError) {
    if (err.stage === "sign") {
      return `Could not get upload permission${err.status ? ` (${err.status})` : ""}: ${err.message.replace(/^Sign failed \(\d+\):\s*/, "").replace(/^Sign endpoint unreachable:\s*/, "")}`;
    }
    if (err.stage === "put") {
      return `File transfer to storage failed${err.status ? ` (${err.status})` : ""}. Try again.`;
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Upload failed. Try again.";
}

/**
 * Persist bytes. Status: pending → uploading → uploaded | failed.
 * Attaches uploadError / uploadSource for UI.
 */
export async function uploadMediaAsset(
  asset: MediaAsset,
  file: Blob,
  userId: string
): Promise<MediaAsset> {
  const uid = (userId || "anonymous").trim() || "anonymous";
  const expectedKey = buildStorageKey({
    userId: uid,
    kind: asset.kind,
    assetId: asset.id,
    fileName: asset.fileName,
    mimeType: asset.mimeType || file.type,
  });

  if (
    asset.uploadStatus === "uploaded" &&
    asset.storageKey === expectedKey &&
    (asset.publicUrl ||
      activeAdapter.id === "local-indexeddb" ||
      activeAdapter.id === "hybrid")
  ) {
    return { ...asset, uploadError: null };
  }

  const uploading: MediaAsset = {
    ...asset,
    uploadStatus: "uploading",
    storageKey: expectedKey,
    uploadError: null,
  };

  try {
    const result = await activeAdapter.upload({
      userId: uid,
      asset: uploading,
      file,
    });

    assertSameStorageKey(expectedKey, result.storageKey);

    let publicUrl = (result.publicUrl || "").trim() || null;
    if (publicUrl && !isHttpsUrl(publicUrl)) {
      try {
        const u = new URL(publicUrl);
        const local =
          u.hostname === "localhost" ||
          u.hostname === "127.0.0.1" ||
          u.hostname === "[::1]";
        if (!(u.protocol === "http:" && local)) {
          publicUrl = null;
        }
      } catch {
        publicUrl = null;
      }
    }

    const via = result.via ?? (activeAdapter.id === "local-indexeddb" ? "local" : "cloud");
    const fallbackWarn =
      via === "local-fallback" ? takeHybridFallbackError() : null;

    return {
      ...uploading,
      storageKey: expectedKey,
      publicUrl,
      objectUrl: publicUrl || uploading.objectUrl,
      uploadStatus: "uploaded",
      uploadSource: via,
      // Soft warning when hybrid fell back — still success for onboarding
      uploadError: fallbackWarn,
      sizeBytes: file.size,
      mimeType: asset.mimeType || file.type || uploading.mimeType,
    };
  } catch (err) {
    console.warn("[un8.media] upload failed:", err);
    return {
      ...asset,
      uploadStatus: "failed",
      uploadSource: null,
      uploadError: formatUploadError(err),
      storageKey:
        asset.storageKey && asset.uploadStatus === "uploaded"
          ? asset.storageKey
          : null,
      publicUrl: asset.uploadStatus === "uploaded" ? asset.publicUrl : null,
    };
  }
}

export async function resolveStoredPreviewUrl(
  asset: MediaAsset
): Promise<string | null> {
  if (asset.publicUrl) {
    const u = asset.publicUrl.trim();
    if (
      isHttpsUrl(u) ||
      u.startsWith("http://localhost") ||
      u.startsWith("http://127.0.0.1") ||
      u.startsWith("/")
    ) {
      // Same-origin private view paths (with or without ticket).
      if (u.startsWith("/")) {
        const { getApiBase } = await import("../api/client");
        return `${getApiBase()}${u}`;
      }
      return u;
    }
  }
  if (asset.objectUrl) return asset.objectUrl;

  // Owner/moderator private fetch by media id — R2 stays private (cookie session).
  if (asset.id) {
    try {
      const { getApiBase } = await import("../api/client");
      const path = `/api/media/${encodeURIComponent(asset.id)}`;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 12_000);
      try {
        const res = await fetch(`${getApiBase()}${path}`, {
          method: "GET",
          credentials: "include",
          signal: ctrl.signal,
        });
        if (res.ok) {
          const blob = await res.blob();
          if (blob.size > 0) return URL.createObjectURL(blob);
        }
        // Fall back to same-origin path so <img> can retry with cookies.
        if (res.status === 401 || res.status === 403) {
          return `${getApiBase()}${path}`;
        }
      } finally {
        clearTimeout(timer);
      }
    } catch {
      /* fall through */
    }
  }

  if (!asset.storageKey) return null;
  return activeAdapter.getPreviewUrl(asset.storageKey);
}

export function toPersistedMediaAsset(asset: MediaAsset): MediaAsset {
  const durableObjectUrl =
    asset.publicUrl ||
    (asset.objectUrl && !asset.objectUrl.startsWith("blob:") ? asset.objectUrl : "");

  return {
    ...asset,
    objectUrl: durableObjectUrl,
    storageKey: asset.storageKey,
    publicUrl: asset.publicUrl,
    uploadStatus:
      asset.uploadStatus ?? (asset.storageKey ? "uploaded" : "pending"),
    uploadSource: asset.uploadSource ?? null,
    // Don't persist transient hybrid warnings forever — optional keep for review
    uploadError: asset.uploadStatus === "failed" ? asset.uploadError ?? null : null,
  };
}

export function mediaIsDurable(asset: MediaAsset | null | undefined): boolean {
  return Boolean(
    asset?.storageKey &&
      (asset.uploadStatus === "uploaded" || asset.publicUrl)
  );
}

export function verificationMediaIsDurable(v: {
  governmentId: MediaAsset | null;
  confirmationVideo: MediaAsset | null;
  profilePhotos: MediaAsset[];
}): boolean {
  if (!mediaIsDurable(v.governmentId)) return false;
  if (!mediaIsDurable(v.confirmationVideo)) return false;
  if (!v.profilePhotos.length) return false;
  return v.profilePhotos.every((p) => mediaIsDurable(p));
}
